import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import {
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  Tooltip,
  Title,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { PaginatedTable } from "../../../components";
import { useAuth } from "../../../contexts/auth";
import { baseAxios, Context } from "../../../utils/config";
import { colors } from "../..";
import {
  chartGrid,
  chartInk,
  chartLabel,
  useThemeMode,
} from "../../../contexts/theme-mode";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  Tooltip,
  Title,
  ChartDataLabels
);

const API = "gateway/oevsb6eif9"; // Development API
// const API = "gateway/eg3p9wnqfb"; // Production API

const dateFieldSx = {
  minWidth: 160,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    borderRadius: "6px",
  },
};

function formatDateToISO(date) {
  if (!date || !dayjs(date).isValid()) return "";
  return dayjs(date).format("YYYY-MM-DD");
}

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n) || n < 0.01) return "$0.00";
  return `$${n.toFixed(2)}`;
}

function formatMoneyChart(value) {
  const n = Number(value);
  if (Number.isNaN(n) || n < 0.01) return "$0";
  if (Math.abs(n - Math.round(n)) < 0.005) return `$${Math.round(n)}`;
  return `$${n.toFixed(2)}`;
}

function validateDateRange(startDate, endDate) {
  if (
    !startDate ||
    !endDate ||
    !dayjs(startDate).isValid() ||
    !dayjs(endDate).isValid()
  ) {
    toast.error("Please select both start and end dates", { duration: 3000 });
    return false;
  }
  if (dayjs(startDate).isAfter(dayjs(endDate), "day")) {
    toast.error("Start date must be before or equal to end date", {
      duration: 3000,
    });
    return false;
  }
  return true;
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

/**
 * Stylist Spending — totals, averages, and transaction counts by stylist
 */
export default function StylistSpend() {
  const { mode } = useThemeMode();
  const { user } = useAuth();
  const { location: globalLocation } = useContext(Context);
  const { state } = useLocation();

  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // The last load failed; an empty table then means "unknown", not "no data".
  const [loadFailed, setLoadFailed] = useState(false);
  // Counts the loads. Typing a date starts one load per keystroke, and an
  // earlier, slower answer must not replace the newest one.
  const latestLoad = useRef(0);
  const [hasRun, setHasRun] = useState(false);
  const [sortBy, setSortBy] = useState("total_spent");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadReport = useCallback(async () => {
    if (!validateDateRange(startDate, endDate)) return;

    const thisLoad = ++latestLoad.current;
    setLoading(true);
    setLoadFailed(false);
    setHasRun(true);
    try {
      const payload = {
        Barcode: user.barcode,
        Role: user.userRoles,
        StartDate: dayjs(startDate).toDate(),
        EndDate: dayjs(endDate).toDate(),
        Organization: parseInt(user.org, 10),
        Salon: {
          location_id:
            globalLocation?.location_id !== -1
              ? globalLocation?.location_id
              : user.location_id,
          location_name:
            state?.location_name ||
            globalLocation?.location_name ||
            user.location_name,
        },
      };

      const result = await baseAxios.post(API, payload);
      if (thisLoad !== latestLoad.current) return;
      const data = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result.data?.data)
          ? result.data.data
          : [];

      setRows(
        data.map((item, index) => ({
          ...item,
          rowKey: `${item.Stylist || "stylist"}-${index}`,
          total_spent: Number(item.total_spent) || 0,
          average_spent: Number(item.average_spent) || 0,
          total_number: Number(item.total_number) || 0,
        }))
      );
    } catch (error) {
      if (thisLoad !== latestLoad.current) return;
      setRows([]);
      setLoadFailed(true);
      // baseAxios rejects with { status, data }, not with an axios error.
      if ((error?.status ?? error?.response?.status) === 403) {
        toast.error("Not allowed to see this data", { duration: 3000 });
      } else {
        toast.error("Error loading stylist spending data", { duration: 3000 });
      }
    } finally {
      if (thisLoad === latestLoad.current) setLoading(false);
    }
  }, [endDate, globalLocation, startDate, state?.location_name, user]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [rows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const chartData = useMemo(() => {
    const labels = sortedRows.map((r) => r.Stylist || "—");
    return {
      labels,
      datasets: [
        {
          type: "bar",
          label: "Total spent",
          data: sortedRows.map((r) => r.total_spent),
          backgroundColor: chartInk(mode),
          yAxisID: "y",
          order: 3,
        },
        {
          type: "bar",
          label: "Number of Transactions",
          data: sortedRows.map((r) => r.total_number),
          backgroundColor: "#8b4e3f",
          yAxisID: "y",
          order: 2,
        },
        {
          type: "bar",
          label: "Average per transaction",
          data: sortedRows.map((r) => r.average_spent),
          backgroundColor: colors.gold,
          yAxisID: "y1",
          order: 1,
        },
      ],
    };
  }, [sortedRows, mode]);

  const chartOptions = useMemo(
    () => {
      const labelColor = chartLabel(mode);
      return {
      responsive: true,
      maintainAspectRatio: false,
      color: labelColor,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          labels: { boxWidth: 12, font: { weight: "600" }, color: labelColor },
        },
        title: {
          display: true,
          text: "Total Stylist Spending",
          color: labelColor,
          font: { size: 14, weight: "600" },
          padding: { bottom: 12 },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              if (ctx.dataset.label === "Number of Transactions") {
                return `${ctx.dataset.label}: ${v}`;
              }
              return `${ctx.dataset.label}: ${formatMoney(v)}`;
            },
          },
        },
        datalabels: {
          display: (ctx) => (Number(ctx.dataset.data[ctx.dataIndex]) || 0) > 0,
          color: (ctx) => {
            if (ctx.dataset.backgroundColor === colors.gold) {
              return mode === "dark" ? "#1a1a1a" : colors.black;
            }
            return "#ffffff";
          },
          font: { weight: "600", size: 10 },
          anchor: "end",
          align: "start",
          formatter: (value, ctx) => {
            if (ctx.dataset.label === "Number of Transactions") return value;
            return formatMoneyChart(value);
          },
        },
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            font: { size: 11 },
            color: labelColor,
          },
          grid: { display: false },
        },
        y: {
          type: "linear",
          position: "left",
          title: {
            display: true,
            text: "Total Spent / Transactions",
            color: labelColor,
          },
          ticks: { color: labelColor },
          beginAtZero: true,
          grid: { color: chartGrid(mode) },
        },
        y1: {
          type: "linear",
          position: "right",
          title: {
            display: true,
            text: "Average Spent per Transaction",
            color: labelColor,
          },
          ticks: { color: labelColor },
          beginAtZero: true,
          grid: { drawOnChartArea: false },
        },
      },
    };
    },
    [mode]
  );

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }

    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Stylist Spending");
      worksheet.columns = [
        { header: "Stylist", key: "Stylist", width: 22 },
        {
          header: "Average Spent per Transaction",
          key: "average_spent",
          width: 28,
        },
        { header: "Total Spent", key: "total_spent", width: 14 },
        { header: "Number of Transactions", key: "total_number", width: 20 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => worksheet.addRow(row));

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `StylistSpending_${formatDateToISO(startDate)}_${formatDateToISO(endDate)}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [endDate, sortedRows, startDate]);

  const columns = useMemo(
    () => [
      {
        id: "Stylist",
        label: "Stylist",
        field: "Stylist",
        minWidth: 180,
        render: (v) => v || "—",
      },
      {
        id: "average_spent",
        label: "Average Spent per Transaction",
        field: "average_spent",
        minWidth: 220,
        align: "right",
        render: (v) => formatMoney(v),
      },
      {
        id: "total_spent",
        label: "Total Spent",
        field: "total_spent",
        minWidth: 140,
        align: "right",
        render: (v) => formatMoney(v),
      },
      {
        id: "total_number",
        label: "Number of Transactions",
        field: "total_number",
        minWidth: 180,
        render: (v) => Number(v) || 0,
      },
    ],
    []
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ px: 8, py: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mt: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Stylist Spending
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total amounts each stylist spent over a period, along with
              averages.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              slotProps={{ textField: { size: "small", sx: dateFieldSx } }}
            />
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              maxDate={dayjs()}
              slotProps={{ textField: { size: "small", sx: dateFieldSx } }}
            />
            <IconButton
              aria-label="Refresh report"
              onClick={loadReport}
              disabled={loading}
              size="medium"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: "6px",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={loading || !sortedRows.length}
              sx={{
                minHeight: 40,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "6px",
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={loadReport}
              disabled={loading}
              sx={{
                minHeight: 40,
                px: 2,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "6px",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? "Loading…" : "Run Report"}
            </Button>
          </Box>
        </Box>

        {sortedRows.length > 0 && (
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#1b1b1e" : "#f2f2f2",
              border: "2px solid",
              borderColor: "secondary.main",
              borderRadius: "6px",
              height: 360,
            }}
          >
            <Chart type="bar" data={chartData} options={chartOptions} />
          </Box>
        )}

        <PaginatedTable
          columns={columns}
          rows={sortedRows}
          totalRowCount={sortedRows.length}
          loading={loading}
          onPaginationChange={noopPagination}
          onSortChange={handleSortChange}
          initialOrderBy="total_spent"
          initialOrder="desc"
          searchQuery={`${formatDateToISO(startDate)}|${formatDateToISO(endDate)}|${globalLocation?.location_id ?? "all"}`}
          clientSidePagination
          skeletonHeight="40px"
          getRowKey={(row) => row.rowKey}
          emptyMessage={
            loadFailed
              ? "Could not load this report. Try again."
              : hasRun
              ? "No stylist spending data for this date range"
              : "Click Run Report to load data"
          }
        />
      </Box>
    </LocalizationProvider>
  );
}
